import { DateTime } from 'luxon';

import { useAppSelector } from 'src/app/store/hooks';
import { useDispatchRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';

import { useUserRoles } from '../../auth/useUserRoles';
import { makeDispatchTimeRange } from './makeDispatchTimeRange';

export const formatDateTime = (time: string, selectedDate: string) => {
  const timeSplit = time.split(':');
  const hour = parseInt(timeSplit[0]);
  const minute = parseInt(timeSplit[1]);
  return DateTime.fromISO(selectedDate).set({ hour, minute }).toUTC().toISO();
};

type DispatchRepositoryOptions = {
  refetchOnMountOrArgChange?: boolean;
  skip?: boolean;
  pollingInterval?: number;
};

/**
 * Note: For testing, be sure to mock not only the /dispatch response,
 * but also mock the router params to have a selectedDate.
 * Depending on your test setup, you may also need to call mockAuth()
 * with dispatcherRoleMembership.
 * Otherwise, the query will be skipped.
 */
export function useDispatchRepository(options: DispatchRepositoryOptions = {}) {
  const selectedDate = useAppSelector((state) => state.dispatcher.selectedDate);
  const today = DateTime.now().toISODate();

  const dispatchCalendarFilters = useAppSelector(
    (state) => state.dispatcher.dispatchCalendarFilters
  );

  const unassignedTripsFilters = useAppSelector(
    (state) => state.dispatcher.unassignedTripsFilters
  );

  const { isAssociate, isRideOrderingPortalUser } = useUserRoles();

  if (isAssociate || isRideOrderingPortalUser || !selectedDate) {
    options = {
      ...options,
      skip: true,
    };
  }

  const unassignedTripsSorting = useAppSelector(
    (state) => state.dispatcher.unassignedTripsSorting
  );
  const unassignedTripsSortingDirection = useAppSelector(
    (state) => state.dispatcher.unassignedTripsSortingDirection
  );

  const dispatchTimeRange = makeDispatchTimeRange(selectedDate || today);
  return useDispatchRetrieveQuery(
    {
      assignedTripStatus: dispatchCalendarFilters.statuses.join(','),
      rangeStart: dispatchTimeRange.start,
      rangeEnd: dispatchTimeRange.end,
      unassignedRangeStart: unassignedTripsFilters.startTime
        ? formatDateTime(
            unassignedTripsFilters.startTime,
            selectedDate || today
          )
        : undefined,
      unassignedRangeEnd: unassignedTripsFilters.endTime
        ? formatDateTime(unassignedTripsFilters.endTime, selectedDate || today)
        : undefined,
      unassignedPayers: unassignedTripsFilters.payers
        .map((payer) => payer.id)
        .join(','),
      unassignedSpaceTypes: unassignedTripsFilters.spaceTypes.join(','),
      unassignedServiceTypes: unassignedTripsFilters.serviceTypes.join(','),
      zipcodeSearch: unassignedTripsFilters.zipCodes.join(','),
      countySearch: unassignedTripsFilters.counties
        .map((county) => `${county.county}:${county.state}`)
        .join(','),
      driverTagSearch: dispatchCalendarFilters.driverTags
        .map((tag) => tag.tag_name)
        .join(','),
      vehicleTagSearch: dispatchCalendarFilters.vehicleTags
        .map((tag) => tag.tag_name)
        .join(','),
      // TODO: should probably rename this to unassignedTripTagSearch
      // to stay consistent with other unassigned trip filters
      tripTagSearch: unassignedTripsFilters.tags
        .map((tag) => tag.tag_name)
        .join(','),
      unassignedOrdering: `${
        unassignedTripsSortingDirection === 'asc' ? '' : '-'
      }${unassignedTripsSorting}`,
    },
    options
  );
}
